// Third Party
import {
  createFileRoute,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
// helpers
import { useAccessPermission } from "@/helpers/acessPermission";
import { aws } from "@/helpers/aws";

// UI
import NotPermission from "@/components/NotPermission";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Pagination from "@/components/Pagination";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Eye } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

// Services and Utils
import { getDirectors } from "@/services/board-of-directors";
import { ErrorResponse } from "@/types/ErrorResponse";
import type { AxiosError } from "axios";

interface BoardPosition {
  boardPosition: string;
}

interface BoardItem {
  id: string;
  firstName: string;
  lastName: string;
  profileUrl: string;
  gender: string;
  agreementDate: string;
  boardPosition?: BoardPosition;
}

interface BoardResponse {
  status: boolean;
  message: string;
  total: number;
  data: BoardItem[];
}

interface EditProps {
  name: string;
  photo: string;
}
interface EditPropsname {
  name: string;

  gender: string;
}
import type { FC } from "react";
import { clearLocalStorage } from "@/helpers/accessLocalStorage";
export const Route = createFileRoute(
  "/_layout-private/report/board-of-directors/"
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      page: Number(search.page) || 1,
    };
  },
});

function RouteComponent() {
  const { page } = Route.useSearch();
  const navigate = useNavigate();
  const companyId = JSON.parse(localStorage.getItem("companyId") || "null");
  const { nonFinancialReport } = useAccessPermission();

  const {
    data: boardsData,
    isLoading,
    error,
  } = useQuery<BoardResponse, AxiosError<ErrorResponse>>({
    queryKey: ["boards", companyId],
    queryFn: () =>
      getDirectors({ companyId, skip: (page - 1) * 40, limit: 40 }),
  });

  if (error) {
    if (error?.response?.data?.message === "LOGGED_OUT") {
      console.log("clearing storage due to logged out");
      clearLocalStorage();
      window.location.reload();
    }
  }

  // Function
  const handlePageClick = (page: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page,
      }),
    } as NavigateOptions);
  };

  const Profile: FC<EditProps> = ({ name, photo }) => {
    return (
      <Avatar>
        <AvatarImage
          src={aws + photo}
          alt={name}
          className="object-cover w-10 h-10 rounded-full"
        />
        <AvatarFallback>
          <span className="text-xl font-semibold text-gray-500">
            {name.charAt(0)}
          </span>
        </AvatarFallback>
      </Avatar>
    );
  };
  const Profilename: FC<EditPropsname> = ({ name, gender }) => {
    return (
      <div className="flex gap-x-2 items-center">
        <Eye size={16} color="#007AFF" />
        <span className="ml-2 text-sm font-medium">
          {gender === "MALE" ? "ທ່ານ " : "ທ່ານ ນາງ "}
          {name}
        </span>
      </div>
    );
  };
  const translateGender = (gender: string) => {
    return gender === "MALE" ? "ຊາຍ" : "ຍິງ";
  };
  // console.log("masterData", masterData);
  return nonFinancialReport.isRead ? (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>ລາຍງານການປະເມີນສະພາບໍລິຫານ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>ລາຍງານການປະເມີນສະພາບໍລິຫານ</CardTitle>
          <CardDescription>ລາຍການລາຍງານການປະເມີນສະພາບໍລິຫານ</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto w-full rounded-sm border">
            <div>
              <Table className="mb-4">
                <TableHeader>
                  <TableRow>
                    <TableHead>ລໍາດັບ</TableHead>
                    <TableHead>ຮູບໂປຣຟາຍ</TableHead>
                    <TableHead>ຊື່ ແລະ ນາມສະກຸນ</TableHead>
                    {/* <TableHead>ບ່ອນສັງກັດ</TableHead> */}

                    <TableHead>ເພດ</TableHead>
                    <TableHead>ຕຳແໜ່ງ</TableHead>
                    <TableHead>ວັນທີແຕ່ງຕັ້ງ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : (
                    boardsData?.data.map((board: BoardItem, index: number) => (
                      <TableRow
                        key={board.id}
                        onClick={() => {
                          try {
                            localStorage.setItem("boardId", board.id);
                            navigate({
                              to: "/report/board-of-directors/details",
                              search: { active: "yourself" },
                            });
                          } catch (error) {
                            console.error("Error saving board ID:", error);
                          }
                        }}
                        className="cursor-pointer"
                      >
                        <TableCell className="font-medium">
                          {index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          <Profile
                            photo={`${board.profileUrl}`}
                            name={`${board.firstName} ${board.lastName}`}
                          />{" "}
                        </TableCell>
                        <TableCell className="font-medium">
                          <Profilename
                            name={`${board.firstName} ${board.lastName}`}
                            gender={`${board.gender}`}
                          />{" "}
                        </TableCell>
                        {/* <TableCell>{board.company.companyName}</TableCell> */}

                        <TableCell>{translateGender(board.gender)}</TableCell>
                        <TableCell>
                          {board.boardPosition?.boardPosition}
                        </TableCell>
                        <TableCell>
                          {new Date(board.agreementDate).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
          <Pagination
            handlePageClick={handlePageClick}
            totalItems={boardsData?.total || 0}
            itemsPerPage={40}
            currentPage={page}
          />
        </CardContent>
      </Card>
    </section>
  ) : (
    <NotPermission />
  );
}
